import axiosInstance from '../auth/axiosInstance';
import { getApiErrorMessage } from '../types/api';
import type { ProjectOversightMode } from '../pages/sa/projectOversight/projectOversightConfig';

type ApiEnvelope<T> = {
  code?: number;
  message?: string;
  data?: T;
};

const unwrap = <T>(result: ApiEnvelope<T> | null | undefined, fallback: string): T => {
  if (result?.code && result.code >= 400) {
    throw new Error(result.message || fallback);
  }
  if (result?.data === undefined || result?.data === null) {
    throw new Error(result?.message || fallback);
  }
  return result.data;
};

export type ProjectOversightClientSummary = {
  clientUserId: number;
  clientName: string;
  designation?: string | null;
  assignedSmeName?: string | null;
  status: string;
  lastActivityUtc?: string | null;
  responseCount?: number | null;
};

export type ProjectOversightAssessmentLine = {
  capabilityName: string;
  subCapabilityName: string;
  currentRating?: number | null;
  futureRating?: number | null;
  currentDescription?: string | null;
  futureDescription?: string | null;
};

export type ProjectOversightAssessmentDetail = {
  clientUserId: number;
  clientName: string;
  status: string;
  assignedSmeName?: string | null;
  lastActivityUtc?: string | null;
  lines: ProjectOversightAssessmentLine[];
};

export type ProjectOversightChatMessage = {
  senderName: string;
  message: string;
  messageDate?: string | null;
  capabilityName?: string | null;
  subCapabilityName?: string | null;
};

export type ProjectOversightChatDetail = {
  clientUserId: number;
  clientName: string;
  assignedSmeName?: string | null;
  messageCount: number;
  lastActivityUtc?: string | null;
  recentMessages: ProjectOversightChatMessage[];
};

export type ProjectOversightReportItem = {
  id?: number | null;
  reportItemId?: string | null;
  ordinalNumber?: number | null;
  remarks?: string | null;
  attachmentUrl?: string | null;
  status?: string | null;
  reportDate?: string | null;
  submittedAt?: string | null;
  members?: Array<{ username?: string; jobTitle?: string }>;
};

const normalizeClientSummary = (raw: Record<string, unknown>): ProjectOversightClientSummary => ({
  clientUserId: Number(raw.clientUserId ?? raw.ClientUserId ?? 0),
  clientName: String(raw.clientName ?? raw.ClientName ?? ''),
  designation: (raw.designation ?? raw.Designation) as string | null | undefined,
  assignedSmeName: (raw.assignedSmeName ?? raw.AssignedSmeName) as string | null | undefined,
  status: String(raw.status ?? raw.Status ?? ''),
  lastActivityUtc: (raw.lastActivityUtc ?? raw.LastActivityUtc) as string | null | undefined,
  responseCount: Number(raw.responseCount ?? raw.ResponseCount ?? 0) || null,
});

const normalizeReport = (raw: Record<string, unknown>): ProjectOversightReportItem => ({
  id: raw.id != null ? Number(raw.id) : raw.Id != null ? Number(raw.Id) : null,
  reportItemId: String(raw.reportItemId ?? raw.ReportItemId ?? ''),
  ordinalNumber:
    raw.ordinalNumber != null
      ? Number(raw.ordinalNumber)
      : raw.OrdinalNumber != null
        ? Number(raw.OrdinalNumber)
        : null,
  remarks: String(raw.remarks ?? raw.Remarks ?? ''),
  attachmentUrl: String(raw.attachmentUrl ?? raw.AttachmentUrl ?? ''),
  status: String(raw.status ?? raw.Status ?? 'submitted'),
  reportDate: (raw.reportDate ?? raw.ReportDate) as string | null | undefined,
  submittedAt: (raw.submittedAt ?? raw.SubmittedAt) as string | null | undefined,
  members: Array.isArray(raw.members ?? raw.Members)
    ? (raw.members ?? raw.Members).map((m: Record<string, unknown>) => ({
        username: String(m.username ?? m.Username ?? ''),
        jobTitle: String(m.jobTitle ?? m.JobTitle ?? ''),
      }))
    : [],
});

const listPath = (mode: ProjectOversightMode, projectId: number) => {
  if (mode === 'report') {
    return `/ProjectOversight/projects/${projectId}/reports`;
  }
  if (mode === 'chat') {
    return `/ProjectOversight/projects/${projectId}/chats`;
  }
  return `/ProjectOversight/projects/${projectId}/assessments`;
};

export const fetchProjectOversightSummaries = async (
  mode: ProjectOversightMode,
  projectId: number
): Promise<ProjectOversightClientSummary[] | ProjectOversightReportItem[]> => {
  try {
    const { data: result } = await axiosInstance.get<ApiEnvelope<unknown>>(listPath(mode, projectId));

    if (mode === 'report') {
      const items = unwrap<unknown[]>(result, 'Failed to load project reports.');
      return (Array.isArray(items) ? items : []).map((item) =>
        normalizeReport(item as Record<string, unknown>)
      );
    }

    const items = unwrap<unknown[]>(result, 'Failed to load project activity.');
    return (Array.isArray(items) ? items : []).map((item) =>
      normalizeClientSummary(item as Record<string, unknown>)
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || 'Failed to load project activity.');
  }
};

export const fetchProjectOversightAssessmentDetail = async (
  projectId: number,
  clientUserId: number
): Promise<ProjectOversightAssessmentDetail> => {
  try {
    const { data: result } = await axiosInstance.get<ApiEnvelope<ProjectOversightAssessmentDetail>>(
      `/ProjectOversight/projects/${projectId}/assessments/${clientUserId}`
    );
    const detail = unwrap(result, 'Failed to load assessment detail.');
    const rawLines = (detail as { lines?: unknown[]; Lines?: unknown[] }).lines
      ?? (detail as { Lines?: unknown[] }).Lines
      ?? [];

    const lines = (Array.isArray(rawLines) ? rawLines : []).map((line) => {
      const row = line as Record<string, unknown>;
      return {
        capabilityName: String(row.capabilityName ?? row.CapabilityName ?? ''),
        subCapabilityName: String(row.subCapabilityName ?? row.SubCapabilityName ?? ''),
        currentRating:
          row.currentRating != null
            ? Number(row.currentRating)
            : row.CurrentRating != null
              ? Number(row.CurrentRating)
              : null,
        futureRating:
          row.futureRating != null
            ? Number(row.futureRating)
            : row.FutureRating != null
              ? Number(row.FutureRating)
              : null,
        currentDescription: String(
          row.currentDescription ?? row.CurrentStatusDescription ?? row.CurrentDescription ?? ''
        ),
        futureDescription: String(
          row.futureDescription ?? row.FutureStatusDescription ?? row.FutureDescription ?? ''
        ),
      };
    });

    return {
      clientUserId: Number(
        (detail as ProjectOversightAssessmentDetail).clientUserId
          ?? (detail as { ClientUserId?: number }).ClientUserId
          ?? 0
      ),
      clientName: String(
        (detail as ProjectOversightAssessmentDetail).clientName
          ?? (detail as { ClientName?: string }).ClientName
          ?? ''
      ),
      status: String(
        (detail as ProjectOversightAssessmentDetail).status
          ?? (detail as { Status?: string }).Status
          ?? ''
      ),
      assignedSmeName:
        (detail as ProjectOversightAssessmentDetail).assignedSmeName
        ?? (detail as { AssignedSmeName?: string }).AssignedSmeName
        ?? null,
      lastActivityUtc:
        (detail as ProjectOversightAssessmentDetail).lastActivityUtc
        ?? (detail as { LastActivityUtc?: string }).LastActivityUtc
        ?? null,
      lines,
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || 'Failed to load assessment detail.');
  }
};

export const fetchProjectOversightChatDetail = async (
  projectId: number,
  clientUserId: number
): Promise<ProjectOversightChatDetail> => {
  try {
    const { data: result } = await axiosInstance.get<ApiEnvelope<ProjectOversightChatDetail>>(
      `/ProjectOversight/projects/${projectId}/chats/${clientUserId}`
    );
    const detail = unwrap(result, 'Failed to load chat detail.');
    return {
      ...detail,
      recentMessages: Array.isArray(detail.recentMessages) ? detail.recentMessages : [],
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || 'Failed to load chat detail.');
  }
};
